<template>
  <v-dialog :value="value" persistent width="1200" @click:outside="close()">
    <v-card>
      <v-card-title class="pl-5 pr-2 pt-4 pb-2">
        <div class="title h6">
          Complex Promo Merging
        </div>
        <v-card-subtitle class="pa-0 ma-0" width="100">
          Select up to 10 eligible promotions for merging.
          <v-btn class="ml-2 mr-2" text @click="close()">
            Cancel
          </v-btn>
          <v-btn class="ma-0" color="primary" :disabled="selectedEligiblePromos.length === 0 || loading" @click="confirmationModal = true">
            Merge
          </v-btn>
        </v-card-subtitle>
      </v-card-title>
      <v-divider class="mx-4"/>
      <v-container fluid class="pa-0">
        <v-data-table
          v-model="selectedEligiblePromos"
          :items="eligiblePromos"
          :headers="headers"
          class="mx-4"
          show-select
          no-data-text="No eligible promotions"
          dense
          item-key="promo_id"
          fixed-header
          :loading="loading"
          :height="tableHeight"
          :footer-props="{'items-per-page-options': [-1]}"
          :items-per-page="-1">
          <template v-slot:[`item.start_dt`]="{ item }">
            <span v-if="item.start_dt">{{$config.formatDate(item.start_dt)}}</span>
            <span v-else>-</span>
          </template>
          <template v-slot:[`item.end_dt`]="{ item }">
            <span v-if="item.end_dt">{{$config.formatDate(item.end_dt)}}</span>
            <span v-else>-</span>
          </template>
          <template v-slot:[`item.updated_on`]="{ item }">
            <span v-if="item.updated_on">{{$config.formatTimestamp(item.updated_on)}}</span>
            <span v-else>-</span>
          </template>
        </v-data-table>
      </v-container>
    </v-card>
    <v-dialog
      v-model="confirmationModal"
      width="400">
      <v-card flat>
        <v-card-title>Confirm Merge</v-card-title>
        <v-card-text class="pb-0">
          Are you sure that you would like to merge with the selected promotions?
        </v-card-text>
        <v-card-actions>
          <v-col>
            <v-row v-if="!isMergedPromo">
              <v-text-field
                v-model="mergedPromoName"
                class="mx-6"
                label="Merged Promo Name"
                dense
                background-color="#fff"
                outlined
                :rules="[v => !!v || 'Promotion name is required', validateText]"
              >
              </v-text-field>
            </v-row>
            <v-row>
              <v-spacer></v-spacer>
              <v-btn class="mr-4 mb-2" @click="confirmationModal = false">
                Cancel
              </v-btn>
              <v-btn color="success" class="white--text mb-2" :disabled="!isMergedPromo && !mergedPromoName" @click="merge()">
                Confirm
              </v-btn>
              <v-spacer></v-spacer>
            </v-row>
          </v-col>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="missingPartiesModal"
      width="600">
      <v-card flat>
        <v-card-title>Promo Party Mismatch</v-card-title>
        <v-card-text class="pb-0">
          One or more of the selected promotions' stores or ad groups do not match the current promo.
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-col>
            <v-subheader>Promos With Differing Parties</v-subheader>
            <v-container
              class="py-0 overflow-y-auto"
              style="max-height: 160px">
                <v-card>
                  <v-list flat nav dense>
                    <v-list-item-group color="primary">
                      <v-list-item
                        v-for="promo in promosWithMissingParties" :key="promo.promo_id"
                        @click="goToPromo(promo.promo_id)">
                        {{ promo.promo_name }}
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-card>
            </v-container>
            <v-subheader>Differing Parties</v-subheader>
            <v-container
              class="py-0 overflow-y-auto"
              style="max-height: 160px">
                <v-card>
                  <v-list disabled dense>
                    <v-list-item-group color="primary">
                      <v-list-item
                        v-for="party in missingParties" :key="party.id">
                        {{ party.name }}
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-card>
            </v-container>
            <v-row>
              <v-spacer></v-spacer>
              <v-btn class="mr-4 mb-2" @click="missingPartiesModal = false">
                Cancel
              </v-btn>
              <v-btn color="warning" class="white--text mb-2" @click="mergeWithWarning()">
                Merge Anyway
              </v-btn>
              <v-spacer></v-spacer>
            </v-row>
          </v-col>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>
<script>
// axios
import Promos from '@/axios/promotion-endpoint'
import Party from '@/axios/party-endpoint'
// mixins
import { utils } from '@/mixins/utils'
import { displayAlert } from '@/mixins/alert'
import { formValidation } from '@/mixins/form-validation'

export default {
  name: 'ComplexMergingModal',
  mixins: [displayAlert, utils, formValidation],
  props: {
    value: Boolean,
    promo: Object,
    promoParties: Array,
    isMergedPromo: {
      type: Boolean,
      default: false
    }
  },
  async created () {
    this.loading = true
    await this.getEligiblePromos()
    this.loading = false
  },
  data() {
    return {
      loading: false,
      mergedPromoName: null,
      confirmationModal: false,
      selectedEligiblePromos: [],
      eligiblePromos: [],
      missingParties: [],
      missingPartiesModal: false,
      skipMissingPartiesCheck: false,
      promosWithMissingParties: [],
      headerProps: {
        sortable: false,
        class: 'accent white--text'
      },
      baseHeaders: [
        { text: '', value: 'data-table-select' },
        { text: 'Promo Name', value: 'promo_name'},
        { text: 'Promo Category', value: 'promo_category_name' },
        { text: 'Start Date', value: 'start_dt' },
        { text: 'End Date', value: 'end_dt' },
        { text: 'Vendor', value: 'vendors' },
        { text: 'Created By', value: 'created_by' },
        { text: 'Updated By', value: 'updated_by' },
        { text: 'Updated On', value: 'updated_on' },
      ]
    }
  },
  computed: {
    headers () {
      return this.baseHeaders.map(header => {
        return { ...this.headerProps, ...header }
      })
    },
    tableHeight () {
      return this.eligiblePromos.length <= 3 ? '300px' : this.eligiblePromos.length <= 7 ? '500px' : '700px'
    },
    categoryManagers () {
      return this.$store.getters.categoryManagers
    }
  },
  methods: {
    close () {
      this.$emit('close')
    },
    goToPromo(promo_id) {
      const route = this.$router.resolve({ path: "/promo", query: { id: promo_id } })
      window.open(route.href)
    },
    async getEligiblePromos () {
      try {
        const { data } = await Promos.findEligiblePromosToMerge(this.promo.id)
        let eligiblePromos = data
        if (eligiblePromos?.length === 0) {
          return this.eligiblePromos = eligiblePromos
        }
        const uniqueVendorPartyIds = [...new Set(eligiblePromos.flatMap(p => p.vendor_party_ids))]
        const payload = {
          party_type: 'VENDOR',
          related_party_ids: uniqueVendorPartyIds
        }
        const results = await Party.searchParties(payload)
        eligiblePromos = eligiblePromos.map(promo => {
          promo.vendors = results.data.flatMap(party => {
            return promo.vendor_party_ids.includes(party?.id) 
              ? `${party.attributes.VENDOR_ID} ${party.name}`
              : []
          }).join(', ')
          return promo
        })
        this.eligiblePromos = eligiblePromos
      } catch (e) {
        this.handleError(e)
      }
    },
    mergeWithWarning () {
      this.missingPartiesModal = false
      this.skipMissingPartiesCheck = true
      this.merge()
    },
    async checkMissingParties () {
      this.missingParties = []
      this.promosWithMissingParties = []
      let missingPartyIds = []
      const promoPartyIds = this.promoParties.flatMap(p => p.store_id)
      this.selectedEligiblePromos.forEach(ep => {
        const notInCurrentPromo = ep.promo_store_party_ids.filter(pid => !promoPartyIds.includes(pid))
        const notInSelectedPromo = promoPartyIds.filter(pid => !ep.promo_store_party_ids.includes(pid))
        if (notInSelectedPromo.length > 0 || notInCurrentPromo.length > 0) {
          missingPartyIds = [...new Set([...missingPartyIds, ...notInCurrentPromo, ...notInSelectedPromo])]
          this.promosWithMissingParties.push(ep)
        }
      })
      if (this.promosWithMissingParties.length > 0) {
        let partiesToFetch = []
        missingPartyIds.forEach(mpid => {
          const missingParty = this.promoParties.find(pp => pp.store_id === mpid)
          if (missingParty) this.missingParties.push(missingParty)
          else partiesToFetch.push(mpid)
        })

        if (partiesToFetch.length > 0) {
          const partyPromises = partiesToFetch.map(pid => Party.getParty(pid))
          const partiesSettled = await this.getAllSettled(partyPromises, true)
          if (partiesSettled.rejected.length > 0) throw partiesSettled.rejected
          partiesSettled.fulfilled.forEach(p => {
            if (p.data) this.missingParties.push(p.data)
          })
        }
        this.missingPartiesModal = true
        return true
      }
      return false
    },
    async merge () {
      this.confirmationModal = false
      if (!this.skipMissingPartiesCheck) {
        const missingParties = await this.checkMissingParties()
        if (missingParties) return
      }
      this.loading = true
      try {
        const payload = this.buildMergePayload()
        const promise = this.isMergedPromo 
          ? Promos.updateMergedPromo(this.promo.id, payload)
          : Promos.mergeComplexPromos(this.promo.id , payload)
        const { data } = await promise
        this.emitAlert(true, 'success', `Promotions have been successfully merged.`, [], true)
        const promoId = this.isMergedPromo ? this.promo.id : data.id
        this.$emit('merged', promoId)
        this.$emit('close')
      } catch (e) {
        this.handleError(e)
      } finally {
        this.loading = false
      }
    },
    buildMergePayload () {
      const selectedPromoIds = this.selectedEligiblePromos.map(ep => ep.promo_id)
      if (this.isMergedPromo) {
        return { promos_to_merge: selectedPromoIds }
      }
      return {
        promos_to_merge: selectedPromoIds,
        promo_name: this.mergedPromoName,
        rebate_amount: Number(this.promo.rebate_amount),
        category_manager_id: this.categoryManagers.find(cm => cm.name === 'Kelly Fields').id,
        ad_date: this.promo.ad_date,
        offer_start_date: this.promo.offer_start_date,
        offer_end_date: this.promo.offer_end_date,
        promotion_price: this.promo.promotion_price,
        complex_promo_price: this.promo.complex_promo_price,
        digital_coupon: this.promo.digital_coupon,
        digital_coupon_note: this.promo.digital_coupon_note,
        perishable: this.promo.perishable,
        dsd: this.promo.dsd
      }
    }
  }
}
</script>
<style>
.header {
  color: white;
  background: #385F73;
}
.title {
  width: 55%;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
  
